/* eslint-disable jsx-a11y/mouse-events-have-key-events */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { TooltipContext } from '#modules/context/TooltipContext';
import styles from './SearchableDropdown.scss';

const { max, min, random } = Math;

function focusNextFormElement(element) {
  if (!element.form) return;
  let elements = [...element.form.elements];
  elements[elements.indexOf(element) + 1]?.focus();
}

const generateId = () => random().toString(36).slice(2);

export default function SearchableDropdown({
  className,
  onChange,
  trigger,
  ariaLabel,
  items,
  search,
  setSearch,
  render,
  disabled,
  isItemDisabled,
  placeholder,
  test,
  testLoading,
  dropdownRelative,
  readOnly,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { dropdownVisible, setDropdownVisible } =
    React.useContext(TooltipContext);

  const id = useMemo(() => generateId(), []);
  const menuId = `menu-${id}`;
  const searchId = `search-${id}`;
  const itemId = (key) => `item-${key}-${id}`;

  const [storedSelectedIndex, setSelectedIndex] = useState(0);

  const selectedIndex = min(items.length - 1, max(0, storedSelectedIndex));

  const open = () => {
    if (!disabled && !readOnly) {
      setIsOpen(true);
      setDropdownVisible(true);
    }
    setSearch('');
  };

  const close = () => {
    setIsOpen(false);
    setDropdownVisible(false);
  };

  const handleKey = (event) => {
    switch (event.key) {
      case 'ArrowUp': {
        let newIndex = selectedIndex - 1;
        while (items?.[newIndex] && isItemDisabled?.(items[newIndex])) {
          // skip disabled items
          newIndex -= 1;
        }
        setSelectedIndex(newIndex);
        event.preventDefault();
        break;
      }
      case 'ArrowDown': {
        let newIndex = selectedIndex + 1;
        while (items?.[newIndex] && isItemDisabled?.(items[newIndex])) {
          // skip disabled items
          newIndex += 1;
        }
        setSelectedIndex(newIndex);
        event.preventDefault();
        break;
      }
      case 'Enter':
        if (!isItemDisabled?.(items[selectedIndex])) {
          onChange(items[selectedIndex]);
          focusNextFormElement(event.target);
        }
        event.preventDefault();
        break;
    }
  };

  return (
    <div
      className={classNames(styles.container, className)}
      data-test={test}
      data-loading={testLoading}
      data-type="searchable"
    >
      <label
        htmlFor={searchId}
        aria-label={ariaLabel}
        className={classNames(styles.trigger, {
          button: !disabled,
          'is-focused': isOpen && !disabled && !readOnly,
          input: disabled,
          'is-readonly': disabled || readOnly,
          [styles.hideOnMobile]: dropdownVisible,
        })}
      >
        {disabled ? null : trigger}
      </label>

      <div
        className={classNames(
          styles.dropdown,
          'dropdown-menu',
          dropdownRelative && styles.relative,
          isOpen && styles.open,
        )}
        role="combobox"
        aria-expanded={isOpen}
        aria-owns={menuId}
        aria-controls={menuId}
      >
        <div
          className={classNames(
            styles.dropdownContent,
            'dropdown-content',
            isOpen && styles.open,
          )}
        >
          <input
            id={searchId}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={handleKey}
            onFocus={open}
            onBlur={close}
            autoComplete="off"
            aria-controls={menuId}
            aria-activedescendant={itemId(selectedIndex)}
            placeholder={placeholder}
          />
          <ul
            className={classNames(styles.dropdownItems)}
            role="listbox"
            id={menuId}
          >
            {items.map((item, index) => {
              const isDisabled = !!isItemDisabled?.(item);
              const isSelected = !isDisabled && selectedIndex === index;
              return (
                <li
                  className={classNames('dropdown-item', styles.dropdownItem, {
                    [styles.active]: isSelected,
                    'dropdown-item-active': isSelected,
                    'is-disabled': isDisabled,
                  })}
                  key={`${index}`}
                  onMouseOver={() => {
                    if (!isDisabled) {
                      setSelectedIndex(index);
                    }
                  }}
                  onMouseDown={(event) => {
                    if (!isDisabled) {
                      onChange(item);
                      setSelectedIndex(index);
                      close();
                    }
                    event.preventDefault();
                  }}
                  onClick={() => {
                    if (!isDisabled) {
                      close();
                      focusNextFormElement(document.activeElement);
                    }
                  }}
                  ref={(element) => {
                    // Only scroll the active item into view while the menu is
                    // open. The list stays in the DOM while closed (opacity
                    // based hiding) and this ref re-runs on every parent
                    // re-render, which scrolled the page to the off-screen
                    // options and made the viewport jump on small screens.
                    if (isOpen && selectedIndex === index) {
                      element?.scrollIntoView({
                        block: 'nearest',
                        behavior: 'auto',
                      });
                    }
                  }}
                  role="option"
                  aria-selected={isSelected}
                  id={itemId(index)}
                >
                  {render(item, isSelected)}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
